import { ComponentProps } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { getIDFromUrl, ID, Url } from '@rossum/api-client';
import { CustomRelation } from '@rossum/api-client/relations';
import { ColumnHeaderPaper } from '@rossum/components/data-grid/ColumnHeaderPaper';
import {
  commonDataGridStyles,
  getDatagridBackgroundStyles,
} from '@rossum/components/data-grid/styles';
import { DetailDrawer } from '@rossum/ui';
import { IconCircleMinus } from '@rossum/ui/icons/tabler';
import {
  Button,
  Fade,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { DataGridPro, GridProSlotsComponent } from '@rossum/ui/x-data-grid-pro';
import { drawerDataGridStyles } from '../../document-list-base/drawerDataGridStyles';
import { SelectAnnotationParams } from '../../document-list-base/supportedAnnotationViews';
import {
  SimpleColumn,
  SimpleColumnFieldNames,
  useSimpleColumns,
} from '../../document-list-columns/hooks/useSimpleColumns';
import { useCreateRelationDialog } from '../create-dialog/useCreateRelationDialog';
import { useCanManageCustomRelations } from '../hooks/useCanManageCustomRelations';
import { useConfirmModal } from '../hooks/useConfirmModal';
import { useCustomRelationsConfig } from '../hooks/useCustomRelationsConfig';
import { useDeleteCustomRelation } from '../hooks/useDeleteCustomRelation';
import { useFetchAnnotationCustomRelations } from '../hooks/useFetchAnnotationCustomRelations';
import { useFetchCustomRelationMembers } from '../hooks/useFetchCustomRelationMembers';
import { useFetchCustomRelationParentNames } from '../hooks/useFetchCustomRelationParentNames';
import { usePatchCustomRelationAnnotations } from '../hooks/usePatchCustomRelationAnnotations';
import { CustomRow } from './CustomRow';

type HandleSelectAnnotation = (params: SelectAnnotationParams) => void;

type CustomRelationDrawerProps = {
  relationId: ID | null;
  annotationUrl: Url | null;
  onClose: () => void;
  handleSelectAnnotation?: HandleSelectAnnotation;
};

const widthMap: Partial<Record<SimpleColumnFieldNames, number>> = {
  status: 150,
  original_file_name: 250,
  queueName: 180,
  labels: 180,
  createdAt: 150,
};

const replaceWidth = ({ width, ...col }: SimpleColumn) => ({
  ...col,
  width: widthMap[col.field] ?? width,
  ...(col.field === 'createdAt' ? { flex: 1 } : {}),
});

export const CustomRelationDrawer = ({
  relationId,
  annotationUrl,
  onClose,
  handleSelectAnnotation,
}: CustomRelationDrawerProps) => {
  const intl = useIntl();

  const { allowedSubtypes } = useCustomRelationsConfig();

  const selectedAnnotationId = annotationUrl
    ? getIDFromUrl(annotationUrl)
    : undefined;

  const { customRelations, isInitialLoading, isError } =
    useFetchAnnotationCustomRelations(selectedAnnotationId ?? null);

  // Without an explicit pre-selection (e.g. when opened from the validation
  // screen topbar), default to the first relation of the annotation.
  const customRelation = relationId
    ? (customRelations.find(relation => relation.id === relationId) ?? null)
    : (customRelations[0] ?? null);

  const {
    parent,
    children,
    isLoading: isLoadingMembers,
    isError: isMembersError,
  } = useFetchCustomRelationMembers(customRelation);

  const getSubtypeLabel = (relation: CustomRelation) =>
    allowedSubtypes.find(subtype => subtype.name === relation.name)?.label ??
    relation.name;

  const subtypeLabel = customRelation ? getSubtypeLabel(customRelation) : '';

  const { parentNameByUrl } =
    useFetchCustomRelationParentNames(customRelations);

  const getRelationOptionLabel = (relation: CustomRelation) => {
    const parentName = relation.parent
      ? parentNameByUrl[relation.parent]
      : undefined;

    return parentName
      ? `${getSubtypeLabel(relation)} — ${parentName}`
      : getSubtypeLabel(relation);
  };

  const isLoading = isInitialLoading || isLoadingMembers;

  const canManageRelations = useCanManageCustomRelations();

  const { openConfirmModal } = useConfirmModal();

  const { mutate: patchRelation, isLoading: isPatching } =
    usePatchCustomRelationAnnotations();

  const { mutate: deleteRelation, isLoading: isDeleting } =
    useDeleteCustomRelation();

  const { node: addToRelationDialog, openDialog: openAddToRelationDialog } =
    useCreateRelationDialog();

  const handleRemoveChild = (childUrl: Url) => {
    if (!customRelation) return;

    // Do not leave an empty relation behind.
    if (children.length === 1) {
      deleteRelation(customRelation.id, { onSuccess: onClose });
      return;
    }

    const remainingChildren = children.flatMap(child =>
      child.url === childUrl ? [] : [child.url]
    );

    patchRelation(
      { relationId: customRelation.id, annotations: remainingChildren },
      {
        onSuccess: () => {
          if (getIDFromUrl(childUrl) === selectedAnnotationId) {
            onClose();
          }
        },
      }
    );
  };

  const columns = useSimpleColumns({
    columnsLocation: 'customRelationsDrawer',
    ...(canManageRelations
      ? {
          removeProps: {
            dataCyPrefix: 'custom-relation',
            Icon: IconCircleMinus,
            onRemove: childUrl =>
              openConfirmModal({
                onConfirm: () => handleRemoveChild(childUrl),
                textId: 'removeCustomRelationDocument',
                confirmType: 'Danger',
              }),
          },
        }
      : {}),
    ...(handleSelectAnnotation
      ? {
          detailsColumnProps: {
            handleSelectAnnotation,
            annotations: R.filter([parent, ...children], R.isTruthy),
          },
        }
      : {}),
  });

  const actionInProgress = isPatching || isDeleting;

  const tables = R.filter(
    [
      parent && {
        rows: [parent],
        // Empty placeholder instead of the remove action keeps tables aligned.
        tableColumns: columns.map(column =>
          column.type === 'actions'
            ? {
                ...column,
                renderCell: () => <Stack width={column.width} height={1} />,
              }
            : replaceWidth(column)
        ),
        title: intl.formatMessage({
          id: 'features.customRelations.drawer.parent.title',
        }),
        isChildrenTable: false,
      },
      {
        rows: children,
        tableColumns: columns.map(replaceWidth),
        title: intl.formatMessage({
          id: 'features.customRelations.drawer.children.title',
        }),
        isChildrenTable: true,
      },
    ],
    R.isTruthy
  );

  return (
    <DetailDrawer
      open={!!annotationUrl}
      title={subtypeLabel}
      onClose={onClose}
      size="large"
    >
      {isLoading ? <LinearProgress variant="indeterminate" /> : null}
      <Fade in={!isLoading}>
        <Stack height={1}>
          {customRelation ? (
            <Stack p={3} height={1} spacing={3}>
              {annotationUrl &&
              handleSelectAnnotation &&
              customRelations.length > 1 ? (
                <TextField
                  select
                  size="small"
                  label={intl.formatMessage({
                    id: 'features.customRelations.drawer.relationSelect',
                  })}
                  value={customRelation.id}
                  onChange={event =>
                    handleSelectAnnotation({
                      annotationUrl,
                      view: 'customRelation',
                      relationId: Number(event.target.value),
                    })
                  }
                  data-cy="custom-relation-drawer-relation-select"
                  sx={{ width: 250, alignSelf: 'flex-start' }}
                >
                  {customRelations.map(relation => (
                    <MenuItem key={relation.id} value={relation.id}>
                      {getRelationOptionLabel(relation)}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              {tables.map(({ rows, tableColumns, title, isChildrenTable }) => (
                <Stack key={title} spacing={2}>
                  <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                    {title}
                  </Typography>
                  <DataGridPro
                    // The parent table should never be in a loading state.
                    loading={isChildrenTable ? actionInProgress : false}
                    columns={tableColumns}
                    rows={rows}
                    disableColumnMenu
                    disableColumnResize
                    disableRowSelectionOnClick
                    autoHeight={rows.length === 0}
                    sx={{
                      ...commonDataGridStyles,
                      ...drawerDataGridStyles,
                      ...getDatagridBackgroundStyles('paper'),
                      '--DataGrid-overlayHeight': '250px',
                    }}
                    slots={{
                      columnHeaders: ColumnHeaderPaper,
                      row: ((props: ComponentProps<typeof CustomRow>) => (
                        <CustomRow
                          {...props}
                          selectedAnnotationId={selectedAnnotationId}
                        />
                      )) as GridProSlotsComponent['row'], // GridProSlotsComponent doesn't accept generics https://v7.mui.com/x/common-concepts/custom-components/#using-additional-props
                      footer: () =>
                        isChildrenTable &&
                        canManageRelations &&
                        customRelation.parent ? (
                          <Stack mt={2}>
                            <Button
                              variant="outlined"
                              color="secondary"
                              sx={{ alignSelf: 'flex-end' }}
                              disabled={actionInProgress}
                              onClick={() => {
                                if (customRelation.parent) {
                                  openAddToRelationDialog(
                                    customRelation.parent,
                                    {
                                      lockedSubtypeName: customRelation.name,
                                      defaultQueueUrl: parent?.queue,
                                    }
                                  );
                                }
                              }}
                              data-cy="custom-relation-drawer-add-button"
                            >
                              {intl.formatMessage({
                                id: 'features.customRelations.drawer.addButton',
                              })}
                            </Button>
                          </Stack>
                        ) : null,
                    }}
                    slotProps={{
                      columnHeaders: {
                        // @ts-expect-error -- elevation is passed through to ColumnHeaderPaper
                        elevation: 0,
                      },
                      loadingOverlay: {
                        variant: 'linear-progress',
                      },
                    }}
                    localeText={{
                      noRowsLabel: intl.formatMessage({
                        id: isMembersError
                          ? ('features.customRelations.drawer.genericError' as const)
                          : ('features.customRelations.drawer.noDocuments' as const),
                      }),
                    }}
                  />
                </Stack>
              ))}
            </Stack>
          ) : isLoading ? null : (
            <Stack alignItems="center" justifyContent="center" height={1}>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({
                  id: isError
                    ? ('features.customRelations.drawer.genericError' as const)
                    : ('features.customRelations.drawer.notFound' as const),
                })}
              </Typography>
            </Stack>
          )}
          {addToRelationDialog}
        </Stack>
      </Fade>
    </DetailDrawer>
  );
};
